import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api/client.js';
import type { AgentShiftState, AgentState, AgentPauseReason } from '@arcnet/contracts';

/**
 * The shift: logging on, what state it is in, and saying it is still there (API agentshifts.ts).
 *
 * The workspace is the only thing that knows a person is at their desk, so it is the workspace
 * that says so -- once a minute while it is open. A shift nobody ends is closed by the sweep at
 * the time it was last heard from, so a closed laptop does not become a ten-hour day.
 *
 * ON A CALL IS NOT A STATE SOMEBODY CHOOSES. The phone knows, so the phone sets it, and going
 * back afterwards returns to whatever was chosen before the call -- ready, or the pause they
 * were already on when it came in.
 */

export interface ShiftApi {
  loaded: boolean;
  /** Null when nobody is logged on at this seat. */
  shiftId: string | null;
  startedAt: string | null;
  state: AgentState;
  reason: string | null;
  stateSince: string | null;
  reasons: AgentPauseReason[];
  busy: boolean;
  error: string | null;
  logOn: () => Promise<void>;
  logOff: () => Promise<void>;
  setState: (state: AgentState, reason?: string | null) => Promise<void>;
}

export function useShift(tenantId: string, extensionId: string, onCall: boolean): ShiftApi {
  const base = `/v1/tenants/${tenantId}/extensions/${extensionId}/shift`;
  const [s, setS] = useState<AgentShiftState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** What to go back to when a call ends: the state that was chosen, not the one the call set. */
  const chosen = useRef<{ state: AgentState; reason: string | null }>({ state: 'ready', reason: null });

  const load = useCallback(async () => {
    const r = await api.get<AgentShiftState>(base);
    setS(r);
    if (r.id !== null && r.state !== 'on_call') chosen.current = { state: r.state, reason: r.reason };
  }, [base]);

  useEffect(() => { void load().catch(() => setS(null)); }, [load]);

  const open = s !== null && s.id !== null;

  /** While the workspace is open, it says so. */
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => { void api.post(`${base}/heartbeat`, {}).catch(() => undefined); }, 60_000);
    return () => clearInterval(t);
  }, [base, open]);

  const put = useCallback(async (state: AgentState, reason: string | null) => {
    await api.put(`${base}/state`, { state, ...(reason ? { reason } : {}) });
    setS((prev) => (prev && prev.id !== null
      ? { ...prev, state, reason, state_since: new Date().toISOString() } : prev));
  }, [base]);

  // The phone's doing, not the person's.
  useEffect(() => {
    if (!open) return;
    const want: AgentState = onCall ? 'on_call' : chosen.current.state;
    const reason = onCall ? null : chosen.current.reason;
    if (s && s.id !== null && (s.state !== want || s.reason !== reason)) {
      void put(want, reason).catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCall, open]);

  const guard = async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try { await fn(); } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work.');
    } finally { setBusy(false); }
  };

  return {
    loaded: s !== null,
    shiftId: s && s.id !== null ? s.id : null,
    startedAt: s && s.id !== null ? s.started_at : null,
    state: s && s.id !== null ? s.state : 'ready',
    reason: s && s.id !== null ? s.reason : null,
    stateSince: s && s.id !== null ? s.state_since : null,
    reasons: s?.reasons ?? [],
    busy,
    error,
    logOn: () => guard(async () => { await api.post(base, {}); await load(); }),
    logOff: () => guard(async () => { await api.delete(base); await load(); }),
    setState: (state, reason = null) => guard(async () => {
      chosen.current = { state, reason };
      await put(state, reason);
    }),
  };
}
